import { Controller } from "@hotwired/stimulus"

// Client-side filter for the Users roster. The list is small and the inputs sit
// outside the turbo-replaced <ul>, so a keystroke can hide rows without a round
// trip — and without create/delete/toggle having to re-learn the current query.
//
// Open domains live here for the same reason: a stream that swaps the rows would
// otherwise collapse every accordion, and the admin who just opened a company
// would lose their place. disclosure itself has no memory, deliberately.
//
// rowTargetConnected re-applies after a stream swaps the rows, which is how a
// filter that was on before the write stays on after it.
export default class extends Controller {
  static targets = ["row", "group", "empty", "query", "role", "count"]

  initialize() {
    this.openDomains = new Set()
  }

  connect() {
    this.apply()
  }

  rowTargetConnected() {
    this.apply()
  }

  emptyTargetConnected() {
    this.apply()
  }

  syncOpen(event) {
    const group = event.currentTarget.closest("[data-settings-users-target='group']")
    if (!group?.dataset.domain) return

    const open = event.currentTarget.getAttribute("aria-expanded") === "true"
    if (open) this.openDomains.add(group.dataset.domain)
    else this.openDomains.delete(group.dataset.domain)
  }

  apply() {
    this.rememberJustAdded()

    const query = this.queryText
    const role = this.roleValue
    const filterActive = query.length > 0 || role !== "all"
    let visibleCount = 0

    this.rowTargets.forEach((row) => {
      const visible = this.matchesQuery(row, query) && this.matchesRole(row, role)
      row.hidden = !visible
      if (visible) visibleCount += 1
    })

    this.groupTargets.forEach((group) => {
      const hasVisibleRow = Boolean(group.querySelector(".settings-row:not([hidden])"))
      group.hidden = !hasVisibleRow
      if (!hasVisibleRow) return

      this.updateCount(group, filterActive)
      this.setGroupOpen(group, filterActive || this.openDomains.has(group.dataset.domain))
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = visibleCount > 0 || this.rowTargets.length === 0
    }
  }

  rememberJustAdded() {
    this.rowTargets.forEach((row) => {
      if (row.dataset.justAdded !== "true") return

      const group = row.closest("[data-settings-users-target='group']")
      if (group?.dataset.domain) this.openDomains.add(group.dataset.domain)
      delete row.dataset.justAdded
    })
  }

  updateCount(group, filterActive) {
    const count = group.querySelector("[data-settings-users-target='count']")
    if (!count) return

    const totalMembers = Number(group.dataset.memberCount || 0)
    const totalAdmins = Number(group.dataset.adminCount || 0)

    if (!filterActive) {
      count.textContent = this.unfilteredSummary(totalMembers, totalAdmins)
      return
    }

    const visibleRows = [ ...group.querySelectorAll(".settings-row:not([hidden])") ]
    const visibleAdmins = visibleRows.filter((row) => row.dataset.admin === "true").length
    count.textContent = this.filteredSummary(visibleRows.length, totalMembers, visibleAdmins, totalAdmins)
  }

  unfilteredSummary(members, admins) {
    const parts = [ this.pluralize(members, "member") ]
    if (admins > 0) parts.push(this.pluralize(admins, "admin"))
    return parts.join(" · ")
  }

  filteredSummary(visibleMembers, totalMembers, visibleAdmins, totalAdmins) {
    const parts = [ `${visibleMembers} of ${totalMembers}` ]
    if (totalAdmins > 0 && visibleAdmins > 0) {
      parts.push(`${visibleAdmins} of ${totalAdmins} ${totalAdmins === 1 ? "admin" : "admins"}`)
    }
    return parts.join(" · ")
  }

  pluralize(count, word) {
    return `${count} ${count === 1 ? word : `${word}s`}`
  }

  // Same open/close as disclosure#setOpen, but without scrollIntoView — applying a
  // filter can open several groups at once, and scrolling through them would jump.
  setGroupOpen(group, open) {
    const content = group.querySelector("[data-disclosure-target='content']")
    const button = group.querySelector("[aria-expanded]")

    if (content) content.hidden = !open
    group.classList.toggle("is-open", open)
    if (button) button.setAttribute("aria-expanded", open ? "true" : "false")
  }

  get queryText() {
    return this.hasQueryTarget ? this.queryTarget.value.trim().toLowerCase() : ""
  }

  get roleValue() {
    return this.hasRoleTarget ? this.roleTarget.value : "all"
  }

  matchesQuery(row, query) {
    if (!query) return true

    return this.searchableText(row).includes(query)
  }

  matchesRole(row, role) {
    if (role === "admins") return row.dataset.admin === "true"
    if (role === "members") return row.dataset.admin !== "true"

    return true
  }

  searchableText(row) {
    return `${row.dataset.name || ""} ${row.dataset.email || ""}`.toLowerCase()
  }
}
